import React, { useEffect, useMemo, useRef, useState } from "react";
import { useApp } from "../contexts/AppContext";

// "Nevão" — a periodic burst of heavier snowfall that repeats every `blizzard_interval`
// seconds and lasts `blizzard_duration` seconds. During each burst a soft synthesized
// wind sound fades in (no audio file needed). All values are configurable in the admin.
export default function BlizzardEffect() {
  const { settings } = useApp();
  const enabled = !!settings?.blizzard_enabled;
  const windOn = settings?.blizzard_wind !== false; // default ON
  const intervalMs = Math.max(8, Number(settings?.blizzard_interval ?? 30)) * 1000;
  const durationMs = Math.max(3, Number(settings?.blizzard_duration ?? 8)) * 1000;
  const startDelayMs = Math.max(0, Number(settings?.blizzard_start_delay ?? 0)) * 1000;
  const randomOn = !!settings?.blizzard_random;
  const randomMaxMs = Math.max(0, Number(settings?.blizzard_random_max ?? 10)) * 1000;
  const INTENSITY = { light: 70, medium: 130, strong: 220 };
  const flakeCount = INTENSITY[settings?.blizzard_intensity] || INTENSITY.medium;

  const [audioReady, setAudioReady] = useState(false);
  const audioRef = useRef(null); // { ctx, gain }

  // Crescendo/decrescendo ramp length (ms): how slowly the snow fades in and out.
  // Fully configurable in the admin ("Suavidade de entrada/saída").
  const ramp = Math.max(300, Math.round(Number(settings?.blizzard_fade ?? 3) * 1000));

  const [mounted, setMounted] = useState(false); // layer rendered (incl. fade-out tail)
  const [on, setOn] = useState(false);           // opacity target 1 (crescendo) / 0 (decrescendo)

  // Periodic cycle: optional start delay, then mount -> crescendo -> hold ->
  // decrescendo -> unmount, repeating every interval (optionally randomized).
  useEffect(() => {
    if (!enabled) { setMounted(false); setOn(false); return; }
    let stopped = false;
    const timers = [];
    const push = (fn, ms) => { const id = setTimeout(fn, ms); timers.push(id); return id; };

    const nextGap = () => {
      if (!randomOn || randomMaxMs <= 0) return intervalMs;
      const jitter = (Math.random() * 2 - 1) * randomMaxMs; // -max .. +max
      return Math.max(8000, intervalMs + jitter);
    };

    const runBurst = () => {
      if (stopped) return;
      setMounted(true);
      // Full timeline: fade-in (ramp) -> intense hold (durationMs) -> fade-out (ramp).
      const offAt = ramp + durationMs;
      push(() => setOn(true), 40);                          // crescendo
      push(() => setOn(false), offAt);                      // decrescendo
      push(() => setMounted(false), offAt + ramp + 120);    // unmount after fade-out
      push(runBurst, nextGap());                            // schedule next cycle
    };

    push(runBurst, startDelayMs);
    return () => { stopped = true; timers.forEach(clearTimeout); };
  }, [enabled, intervalMs, durationMs, ramp, startDelayMs, randomOn, randomMaxMs]);

  // Build a soft, gusty wind sound from filtered noise (Web Audio). Needs a user gesture.
  useEffect(() => {
    if (!enabled || !windOn) return;
    let disposed = false;

    const ensureAudio = () => {
      if (audioRef.current || disposed) return;
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        const bufferSize = 2 * ctx.sampleRate;
        const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        let b0 = 0, b1 = 0, b2 = 0; // pink-ish noise for a natural rumble
        for (let i = 0; i < bufferSize; i++) {
          const white = Math.random() * 2 - 1;
          b0 = 0.99 * b0 + white * 0.029;
          b1 = 0.96 * b1 + white * 0.043;
          b2 = 0.57 * b2 + white * 0.15;
          data[i] = (b0 + b1 + b2) * 0.4;
        }
        const src = ctx.createBufferSource();
        src.buffer = buffer; src.loop = true;
        const filter = ctx.createBiquadFilter();
        filter.type = "lowpass"; filter.frequency.value = 600; filter.Q.value = 0.7;
        const gain = ctx.createGain(); gain.gain.value = 0.0001;
        // Slow LFO sweeps the filter for a gusting feel.
        const lfo = ctx.createOscillator(); lfo.frequency.value = 0.18;
        const lfoGain = ctx.createGain(); lfoGain.gain.value = 320;
        lfo.connect(lfoGain); lfoGain.connect(filter.frequency);
        src.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
        src.start(); lfo.start();
        audioRef.current = { ctx, gain };
        setAudioReady(true);
      } catch (e) { /* audio unsupported */ }
    };

    const onGesture = () => {
      ensureAudio();
      const ctx = audioRef.current?.ctx;
      if (ctx && ctx.state === "suspended") ctx.resume();
    };
    window.addEventListener("pointerdown", onGesture);
    window.addEventListener("keydown", onGesture);
    window.addEventListener("scroll", onGesture, { passive: true });
    ensureAudio(); // try immediately (works if audio is already allowed)
    return () => {
      disposed = true;
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
      window.removeEventListener("scroll", onGesture);
    };
  }, [enabled, windOn]);

  // Fade the wind in/out together with the snow crescendo/decrescendo.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const { ctx, gain } = a;
    const now = ctx.currentTime;
    const target = (on && windOn && enabled) ? 0.06 : 0.0001;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(Math.max(gain.gain.value, 0.0001), now);
    gain.gain.exponentialRampToValueAtTime(target, now + ramp / 1000);
  }, [on, windOn, enabled, audioReady, ramp]);

  // Tear down the audio graph on unmount.
  useEffect(() => () => {
    const a = audioRef.current;
    if (a?.ctx) { try { a.ctx.close(); } catch (e) { /* noop */ } audioRef.current = null; }
  }, []);

  // Heavier, faster flakes; staggered delays make each burst build up in intensity.
  // Count is driven by the configured intensity (light / medium / strong).
  const flakes = useMemo(
    () => Array.from({ length: flakeCount }).map((_, i) => ({
      left: Math.random() * 100,
      size: 6 + Math.random() * 12,
      duration: 2.2 + Math.random() * 2.6,
      delay: -0.4 + (i / flakeCount) * 2.6 + Math.random() * 0.4,
      opacity: 0.5 + Math.random() * 0.5,
    })),
    [flakeCount]
  );

  if (!enabled || !mounted) return null;
  return (
    <div
      className="blizzard-layer pointer-events-none fixed inset-0 z-[60] overflow-hidden"
      data-testid="blizzard-effect"
      style={{ opacity: on ? 1 : 0, transition: `opacity ${ramp}ms ease-in-out` }}
    >
      {flakes.map((f, i) => (
        <span
          key={i}
          className="blizzard-flake"
          style={{
            left: `${f.left}%`,
            width: `${f.size}px`,
            height: `${f.size}px`,
            opacity: f.opacity,
            animationDuration: `${f.duration}s`,
            animationDelay: `${f.delay}s`,
          }}
        />
      ))}
    </div>
  );
}
